import React, { useState } from 'react';
import Card from './shared/Card';

const ConfigurationPanel = ({ config, setConfig, financials, setFinancials, onRunSimulation }) => {
  const [activeTab, setActiveTab] = useState('system');
  const [isExpanded, setIsExpanded] = useState(false);

  // Handle system config changes
  const handleSystemChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  // Handle CAPEX changes
  const handleCapexChange = (field, value) => {
    setFinancials(prev => ({
      ...prev,
      capex: {
        ...prev.capex,
        [field]: parseFloat(value) || 0
      }
    }));
  };

  // Handle OPEX changes
  const handleOpexChange = (field, value) => {
    setFinancials(prev => ({
      ...prev,
      opex: {
        ...prev.opex,
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const systemConfig = [
    { field: 'heater_MW', label: 'Elkedel kapacitet', unit: 'MW', min: 10, max: 50, step: 5 },
    { field: 'htes_modules', label: 'HTES moduler', unit: 'stk', min: 1, max: 4, step: 1 },
    { field: 'ltes_capacity', label: 'LTES kapacitet', unit: 'MWh', min: 100, max: 500, step: 5 },
    { field: 'turbine_MW', label: 'Turbine kapacitet', unit: 'MW', min: 5, max: 20, step: 1 },
    { field: 'battery_MWh', label: 'Batteri kapacitet', unit: 'MWh', min: 1, max: 10, step: 1 }
  ];

  const capexItems = [
    { field: 'tilslutning', label: 'Tilslutning', group: 'infrastruktur' },
    { field: 'bygninger', label: 'Bygninger', group: 'infrastruktur' },
    { field: 'htes', label: 'HTES system', group: 'lager' },
    { field: 'akkumuleringstank', label: 'LTES tank', group: 'lager' },
    { field: 'varmesystem', label: 'Elkedel', group: 'produktion' },
    { field: 'turbine', label: 'ORC Turbine', group: 'produktion' },
    { field: 'battery', label: 'Batteri', group: 'produktion' },
    { field: 'rorføring', label: 'Rørføring', group: 'infrastruktur' },
    { field: 'projektledelse', label: 'Projektledelse', group: 'admin' },
    { field: 'unit', label: 'Unit/styring', group: 'admin' },
    { field: 'software', label: 'Software', group: 'admin' },
    { field: 'fjernvarmepulje', label: 'Fjernvarmepulje tilskud', group: 'tilskud' },
    { field: 'tilslutningsbidrag', label: 'Tilslutningsbidrag', group: 'tilskud' }
  ];

  const opexItems = [
    { field: 'personnel', label: 'Personale (2 FTE)' },
    { field: 'chemicals', label: 'Kemikalier' },
    { field: 'vessAI', label: 'Vess AI optimering' },
    { field: 'insurance', label: 'Forsikring' },
    { field: 'nitrogen', label: 'Nitrogen' },
    { field: 'general', label: 'Øvrige omkostninger' }
  ];

  const formatNumber = (num) => {
    return new Intl.NumberFormat('da-DK').format(Math.round(num));
  };

  return (
    <div className="configuration-panel">
      <Card 
        title="Konfiguration & Dimensionering" 
        color="gray"
        className="config-card"
      >
        {/* Expand/Collapse Toggle */}
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm text-blue-400 hover:text-blue-300"
          >
            {isExpanded ? '▼ Skjul detaljer' : '▶ Vis detaljer'}
          </button>
          <button
            onClick={onRunSimulation}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
          >
            Kør Simulation
          </button>
        </div>

        {isExpanded && (
          <>
            {/* Tab Navigation */}
            <div className="flex space-x-1 mb-4 border-b border-gray-600">
              <button
                onClick={() => setActiveTab('system')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'system' 
                    ? 'text-blue-400 border-b-2 border-blue-400' 
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                System Dimensionering
              </button>
              <button
                onClick={() => setActiveTab('capex')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'capex' 
                    ? 'text-blue-400 border-b-2 border-blue-400' 
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                CAPEX ({formatNumber(financials.capex.total)} DKK)
              </button>
              <button
                onClick={() => setActiveTab('opex')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'opex' 
                    ? 'text-blue-400 border-b-2 border-blue-400' 
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                OPEX ({formatNumber(financials.opex.total)} DKK/år)
              </button>
            </div>

            {/* Tab Content */}
            <div className="config-content">
              {/* System Configuration Tab */}
              {activeTab === 'system' && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {systemConfig.map(item => (
                    <div key={item.field} className="config-item">
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        {item.label}
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="range"
                          min={item.min}
                          max={item.max}
                          step={item.step}
                          value={config[item.field]}
                          onChange={(e) => handleSystemChange(item.field, e.target.value)}
                          className="flex-1"
                        />
                        <div className="w-20 text-right">
                          <span className="text-lg font-semibold text-white">
                            {config[item.field]}
                          </span>
                          <span className="text-sm text-gray-400 ml-1">{item.unit}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* CAPEX Configuration Tab */}
              {activeTab === 'capex' && (
                <div className="space-y-4">
                  {['infrastruktur', 'lager', 'produktion', 'admin', 'tilskud'].map(group => (
                    <div key={group}>
                      <h4 className="text-sm font-semibold text-gray-400 uppercase mb-2">
                        {group === 'tilskud' ? 'Tilskud (negative værdier)' : group}
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {capexItems.filter(item => item.group === group).map(item => (
                          <div key={item.field} className="config-item">
                            <label className="block text-xs text-gray-400 mb-1">
                              {item.label}
                            </label>
                            <input
                              type="number"
                              value={financials.capex[item.field]}
                              onChange={(e) => handleCapexChange(item.field, e.target.value)}
                              className="w-full px-2 py-1 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-400 focus:outline-none text-sm"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="mt-4 pt-4 border-t border-gray-600">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Afskrivningsperiode</span>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="10"
                          max="30"
                          value={financials.capex.years}
                          onChange={(e) => handleCapexChange('years', e.target.value)}
                          className="w-16 px-2 py-1 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-400 focus:outline-none text-sm"
                        />
                        <span className="text-sm text-gray-400">år</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* OPEX Configuration Tab */}
              {activeTab === 'opex' && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {opexItems.map(item => (
                    <div key={item.field} className="config-item">
                      <label className="block text-sm text-gray-400 mb-1">
                        {item.label}
                      </label>
                      <input
                        type="number"
                        value={financials.opex[item.field]}
                        onChange={(e) => handleOpexChange(item.field, e.target.value)}
                        className="w-full px-2 py-1 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-400 focus:outline-none"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Quick Summary (always visible) */}
        <div className="mt-4 pt-4 border-t border-gray-600 grid grid-cols-5 gap-4 text-sm">
          <div>
            <span className="text-gray-400">Elkedel:</span>
            <span className="ml-2 font-semibold">{config.heater_MW} MW</span>
          </div>
          <div>
            <span className="text-gray-400">HTES:</span>
            <span className="ml-2 font-semibold">{config.htes_modules * 150} MWh</span>
          </div>
          <div>
            <span className="text-gray-400">LTES:</span>
            <span className="ml-2 font-semibold">{config.ltes_capacity} MWh</span>
          </div>
          <div>
            <span className="text-gray-400">Turbine:</span>
            <span className="ml-2 font-semibold">{config.turbine_MW} MW</span>
          </div>
          <div>
            <span className="text-gray-400">Batteri:</span>
            <span className="ml-2 font-semibold">{config.battery_MWh} MWh</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ConfigurationPanel;